import { useMemo, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ApiError,
  apiFetch,
  getAdminLiveConfig,
  getAdminLiveDiagnostics,
  killAdminStream,
  rotateAdminLiveKey,
  rotateTeamStreamKey,
  unblockAdminStream,
  updateAdminSettings,
} from "@/lib/api";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { unscopedKey } from "@/lib/queryClient";
import type {
  AdminLiveConfig,
  AdminLiveDiagnostics,
  AdminDiagnostics,
  AdminPerformance,
  AdminStreamBlock,
  AdminStreamSession,
} from "@/lib/types";
import {
  useAdminDiagnostics,
  useAdminPerformance,
  useAdminStreams,
} from "../hooks/useAdminData";

/**
 * M8a C1–C7 — `/admin/live` broadcast cockpit.
 *
 * Mounts the 5 s `useAdminPerformance` (encoder telemetry +
 * throughput) AND swaps the shell's 10 s `useAdminStreams` to 5 s
 * cadence while on this route. The shared cache key means both
 * intervals can coexist — last one wins per query.
 *
 * Layout:
 *   - Left rail: ingest config card (RTMP URL + masked stream key +
 *     rotate-key + offline message + master enable toggle)
 *   - Right rail: ON-AIR pill, encoder load tile, live throughput
 *     sparkline, viewers list (with Kill per row), blocks list,
 *     diagnostics block (MediaMTX + live activity)
 *     (with Unblock per row)
 */
export function AdminLivePage() {
  // Re-mount the streams query at 5 s for this route (C7).
  const streamsQuery = useAdminStreams({ refetchIntervalMs: 5_000 });
  const perfQuery = useAdminPerformance({ refetchIntervalMs: 5_000 });
  const diagnosticsQuery = useAdminDiagnostics({ refetchIntervalMs: 10_000 });
  const liveDiagnosticsQuery = useQuery({
    queryKey: unscopedKey("admin-live-diagnostics"),
    queryFn: getAdminLiveDiagnostics,
    refetchInterval: 10_000,
    refetchIntervalInBackground: false,
  });
  const configQuery = useQuery({
    queryKey: unscopedKey("admin-live-config"),
    queryFn: getAdminLiveConfig,
  });

  const liveSessions =
    streamsQuery.data?.active?.filter((s) => s.kind === "live") ?? [];
  const blocks = streamsQuery.data?.blocks ?? [];

  return (
    <div data-testid="admin-live" className="flex flex-col gap-3">
      <header className="flex items-baseline justify-between">
        <h2 className="text-base font-display uppercase tracking-wider text-ink">
          Live
        </h2>
        <span className="text-xs font-mono text-ink-muted">
          {perfQuery.isFetching || streamsQuery.isFetching
            ? "Refreshing…"
            : "Auto-refreshes every 5 s"}
        </span>
      </header>

      {/* M12c — per-team stream key roster. Sits above the legacy global
       * IngestConfigCard so admins see the per-team surface first. */}
      <AdminTeamsLiveStrip />

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,360px)_1fr] gap-3">
        <IngestConfigCard query={configQuery} />
        <div className="flex flex-col gap-3 min-w-0">
          <OnAirAndThroughputRow
            sessions={liveSessions}
            perf={perfQuery.data}
            configEnabled={!!configQuery.data?.enabled}
          />
          <EncoderLoadTile perf={perfQuery.data} />
          <ViewersList sessions={liveSessions} />
          <BlocksList blocks={blocks} />
          <LiveDiagnosticsBlock
            diagnostics={diagnosticsQuery.data}
            liveDiagnostics={liveDiagnosticsQuery.data}
            liveDiagnosticsFetching={liveDiagnosticsQuery.isFetching}
            liveDiagnosticsError={liveDiagnosticsQuery.error}
            onRefresh={() => void liveDiagnosticsQuery.refetch()}
          />
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// C1 — Ingest config card
// ---------------------------------------------------------------------------

function IngestConfigCard({
  query,
}: {
  query: ReturnType<typeof useQuery<AdminLiveConfig>>;
}) {
  const queryClient = useQueryClient();
  const config = query.data;
  const [revealKey, setRevealKey] = useState(false);
  const [rotateOpen, setRotateOpen] = useState(false);
  const [offlineDraft, setOfflineDraft] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const saveSettings = useMutation({
    mutationFn: (body: Record<string, unknown>) => updateAdminSettings(body),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: unscopedKey("admin-live-config"),
      });
      setOfflineDraft(null);
    },
    onError: (err) => setError(formatErr(err)),
  });

  const rotateKey = useMutation({
    mutationFn: rotateAdminLiveKey,
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: unscopedKey("admin-live-config"),
      });
      setRotateOpen(false);
    },
    onError: (err) => setError(formatErr(err)),
  });

  return (
    <section
      data-testid="admin-live-ingest"
      className="flex flex-col gap-3 rounded-lg border border-rule bg-bg-card/40 p-3 h-fit"
    >
      <header className="flex items-baseline justify-between">
        <h3 className="text-sm font-display uppercase tracking-wider text-ink">
          Ingest
        </h3>
        <span className="text-[0.6rem] font-mono text-ink-muted">
          {query.isLoading ? "Loading…" : config?.enabled ? "Enabled" : "Disabled"}
        </span>
      </header>

      {error ? (
        <p
          data-testid="admin-live-ingest-error"
          className="text-destructive font-mono text-xs"
        >
          {error}
        </p>
      ) : null}

      <button
        type="button"
        onClick={() => {
          setError(null);
          saveSettings.mutate({ live_enabled: config?.enabled ? "0" : "1" });
        }}
        disabled={query.isLoading || saveSettings.isPending}
        data-testid="admin-live-enabled"
        data-active={!!config?.enabled ? "true" : undefined}
        className="self-start rounded border px-3 py-1.5 text-xs font-display uppercase tracking-wider transition-colors border-rule text-ink-muted hover:text-ink hover:bg-surface data-[active=true]:border-accent data-[active=true]:text-accent data-[active=true]:bg-accent/10 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {config?.enabled ? "Live enabled" : "Live disabled"}
      </button>

      <div className="flex flex-col gap-1">
        <span className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
          RTMP URL
        </span>
        <code
          data-testid="admin-live-rtmp-url"
          className="break-all rounded border border-rule bg-surface px-2 py-1.5 text-[0.65rem] font-mono text-ink"
        >
          {config?.rtmp_public_url || "—"}
        </code>
      </div>

      <div className="flex flex-col gap-1">
        <span className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
          Stream Key
        </span>
        <div className="flex items-center gap-1">
          <code
            data-testid="admin-live-stream-key"
            data-revealed={revealKey ? "true" : "false"}
            className="break-all rounded border border-rule bg-surface px-2 py-1.5 text-[0.65rem] font-mono text-ink grow"
          >
            {revealKey
              ? config?.stream_key || "—"
              : config?.stream_key
                ? "•".repeat(Math.min(24, config.stream_key.length))
                : "—"}
          </code>
          <button
            type="button"
            onClick={() => setRevealKey((v) => !v)}
            data-testid="admin-live-stream-key-toggle"
            className="rounded border border-rule text-ink-muted hover:text-ink hover:bg-surface px-2 py-1.5 text-[0.6rem] font-display uppercase tracking-wider"
          >
            {revealKey ? "Hide" : "Show"}
          </button>
        </div>
        <button
          type="button"
          onClick={() => setRotateOpen(true)}
          data-testid="admin-live-rotate-key"
          className="self-start rounded border border-destructive/40 text-destructive hover:bg-destructive/10 px-2 py-0.5 text-[0.6rem] font-display uppercase tracking-wider"
        >
          Rotate key
        </button>
      </div>

      <div className="flex flex-col gap-1">
        <span className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
          Offline message
        </span>
        <textarea
          value={offlineDraft ?? config?.offline_message ?? ""}
          onChange={(e) => setOfflineDraft(e.target.value)}
          rows={3}
          data-testid="admin-live-offline-message"
          className="rounded border border-rule bg-surface px-2 py-1.5 text-xs text-ink focus:outline-none focus:ring-2 focus:ring-accent"
        />
        {offlineDraft !== null ? (
          <div className="flex gap-1">
            <button
              type="button"
              onClick={() => {
                setError(null);
                saveSettings.mutate({ live_offline_message: offlineDraft });
              }}
              disabled={saveSettings.isPending}
              data-testid="admin-live-offline-save"
              className="rounded border border-accent/40 text-accent hover:bg-accent/10 px-2 py-1 text-[0.6rem] font-display uppercase tracking-wider disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Save
            </button>
            <button
              type="button"
              onClick={() => setOfflineDraft(null)}
              className="rounded border border-rule text-ink-muted hover:text-ink hover:bg-surface px-2 py-1 text-[0.6rem] font-display uppercase tracking-wider"
            >
              Cancel
            </button>
          </div>
        ) : null}
      </div>

      {rotateOpen ? (
        <ConfirmDialog
          title="Rotate stream key?"
          message="The current key becomes invalid immediately. Any encoder still streaming with the old key will be disconnected. You'll need to reconfigure your encoder with the new key."
          confirmLabel={rotateKey.isPending ? "Rotating…" : "Rotate key"}
          danger
          onConfirm={() => {
            setError(null);
            rotateKey.mutate();
          }}
          onClose={() => setRotateOpen(false)}
          data-testid="admin-live-rotate-confirm"
        />
      ) : null}
    </section>
  );
}

// ---------------------------------------------------------------------------
// C2 + C5 — ON-AIR pill + throughput sparkline
// ---------------------------------------------------------------------------

function OnAirAndThroughputRow({
  sessions,
  perf,
  configEnabled,
}: {
  sessions: AdminStreamSession[];
  perf: AdminPerformance | undefined;
  configEnabled: boolean;
}) {
  const onAir =
    configEnabled && sessions.length > 0
      ? "on-air"
      : configEnabled
        ? "standby"
        : "off-air";
  const liveCount = sessions.length;
  const samples = perf?.throughput?.samples ?? [];
  const lastBps = Number(perf?.throughput?.last?.bps_live_out ?? 0);
  const avgBps = Number(perf?.throughput?.avg_live_bps_30s ?? 0);

  return (
    <section
      data-testid="admin-live-onair-row"
      className="rounded-lg border border-rule bg-bg-card/40 p-3"
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <OnAirPill state={onAir} viewers={liveCount} />
        <div className="flex flex-col items-end">
          <span className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
            Live throughput
          </span>
          <span
            data-testid="admin-live-throughput"
            className="text-sm font-mono text-ink"
          >
            {formatBps(lastBps)} <span className="text-ink-muted">· 30s avg {formatBps(avgBps)}</span>
          </span>
        </div>
      </div>
      <ThroughputSparkline samples={samples.map((s) => Number(s.bps_live_out ?? 0))} />
    </section>
  );
}

function OnAirPill({
  state,
  viewers,
}: {
  state: "off-air" | "standby" | "on-air";
  viewers: number;
}) {
  const tone =
    state === "on-air"
      ? "border-destructive/60 text-destructive bg-destructive/10"
      : state === "standby"
        ? "border-accent/40 text-accent bg-accent/10"
        : "border-rule text-ink-muted";
  const label =
    state === "on-air" ? "ON AIR" : state === "standby" ? "STANDBY" : "OFF AIR";
  return (
    <span
      data-testid="admin-live-onair-pill"
      data-state={state}
      className={`inline-flex items-center gap-2 rounded border px-3 py-1 text-xs font-display uppercase tracking-wider ${tone}`}
    >
      <span className="size-1.5 rounded-full bg-current" />
      <span>{label}</span>
      {state === "on-air" ? (
        <span className="font-mono text-[0.65rem]">· {viewers} viewer{viewers === 1 ? "" : "s"}</span>
      ) : null}
    </span>
  );
}

function ThroughputSparkline({ samples }: { samples: number[] }) {
  if (samples.length === 0) {
    return (
      <p className="mt-2 text-[0.6rem] font-mono text-ink-muted">
        No throughput samples yet.
      </p>
    );
  }
  const data = samples.slice(-60);
  const max = Math.max(...data, 1);
  const W = 600;
  const H = 40;
  const step = data.length > 1 ? W / (data.length - 1) : W;
  const points = data
    .map((v, i) => `${(i * step).toFixed(1)},${(H - (v / max) * H).toFixed(1)}`)
    .join(" ");
  return (
    <svg
      data-testid="admin-live-throughput-sparkline"
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="none"
      className="mt-2 w-full h-10"
      aria-label="Live throughput last 60 samples"
    >
      <polyline
        fill="none"
        stroke="currentColor"
        strokeWidth={1.5}
        points={points}
        className="text-accent"
      />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// C6 — Encoder load tile
// ---------------------------------------------------------------------------

function EncoderLoadTile({ perf }: { perf: AdminPerformance | undefined }) {
  const host = (perf?.host ?? {}) as Record<string, unknown>;
  const cpu = Number(host.cpu_percent ?? 0);
  const mem = Number(getNested(host, ["memory", "percent"]) ?? 0);
  const gpu = Number(host.intel_gpu_busy_pct ?? 0);
  const txBps = Number(getNested(host, ["net", "bps_tx"]) ?? 0);
  const vodAvgBps = Number(perf?.throughput?.avg_vod_bps_30s ?? 0);
  const concurrency = Number(perf?.transcode?.concurrency_limit ?? 0);

  return (
    <section
      data-testid="admin-live-encoder-tile"
      className="grid grid-cols-2 lg:grid-cols-3 gap-2 rounded-lg border border-rule bg-bg-card/40 p-3"
    >
      <Stat label="CPU" value={`${cpu.toFixed(0)}%`} />
      <Stat label="iGPU" value={`${gpu.toFixed(0)}%`} />
      <Stat label="Memory" value={`${mem.toFixed(0)}%`} />
      <Stat label="NIC TX" value={formatBps(txBps)} />
      <Stat label="VOD avg" value={formatBps(vodAvgBps)} />
      <Stat label="Encoder slots" value={String(concurrency)} />
    </section>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col">
      <span className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
        {label}
      </span>
      <span className="text-sm font-mono text-ink">{value}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// C3 — Viewers list (with Kill)
// ---------------------------------------------------------------------------

function ViewersList({ sessions }: { sessions: AdminStreamSession[] }) {
  const [killTarget, setKillTarget] = useState<AdminStreamSession | null>(null);
  return (
    <section
      data-testid="admin-live-viewers"
      className="flex flex-col gap-2 rounded-lg border border-rule bg-bg-card/40 p-3"
    >
      <header className="flex items-baseline justify-between">
        <h3 className="text-sm font-display uppercase tracking-wider text-ink">
          Active viewers
        </h3>
        <span className="text-[0.6rem] font-mono text-ink-muted">
          {sessions.length} live
        </span>
      </header>
      {sessions.length === 0 ? (
        <p
          data-testid="admin-live-viewers-empty"
          className="text-xs font-mono text-ink-muted"
        >
          No live viewers connected.
        </p>
      ) : (
        <ul className="flex flex-col gap-1">
          {sessions.map((s) => (
            <li
              key={s.session_id}
              data-testid="admin-live-viewer-row"
              data-session-id={s.session_id}
              className="grid grid-cols-[1fr_auto] gap-2 items-center rounded border border-rule/60 bg-surface/40 px-2 py-1.5"
            >
              <div className="flex flex-col text-[0.6rem] font-mono text-ink-muted min-w-0">
                <span className="text-ink truncate">
                  {s.ip}{" "}
                  {s.geo_country ? (
                    <span className="text-ink-muted">· {s.geo_country}</span>
                  ) : null}
                </span>
                <span>
                  {formatBytesShort(Number(s.bytes_sent ?? 0))} ·{" "}
                  {Math.round(Number(s.duration_seconds ?? 0))}s active ·{" "}
                  {Math.round(Number(s.idle_seconds ?? 0))}s idle
                </span>
              </div>
              <button
                type="button"
                onClick={() => setKillTarget(s)}
                data-testid="admin-live-viewer-kill"
                className="rounded border border-destructive/40 text-destructive hover:bg-destructive/10 px-2 py-0.5 text-[0.65rem] font-display uppercase tracking-wider"
              >
                Kill
              </button>
            </li>
          ))}
        </ul>
      )}
      {killTarget ? (
        <KillViewerConfirm
          session={killTarget}
          onClose={() => setKillTarget(null)}
        />
      ) : null}
    </section>
  );
}

function KillViewerConfirm({
  session,
  onClose,
}: {
  session: AdminStreamSession;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const kill = useMutation({
    mutationFn: () => killAdminStream(session.session_id),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: unscopedKey("admin-streams"),
      });
      onClose();
    },
    onError: (err) => setError(formatErr(err)),
  });
  return (
    <ConfirmDialog
      title="Kill this viewer session?"
      message={
        <>
          <p>
            Disconnects <strong>{session.ip}</strong> and short-lists the (ip,
            target) for ~30 s so they can't immediately reconnect.
          </p>
          {error ? (
            <p
              data-testid="admin-live-kill-error"
              className="mt-2 text-destructive font-mono text-xs"
            >
              {error}
            </p>
          ) : null}
        </>
      }
      confirmLabel={kill.isPending ? "Killing…" : "Kill session"}
      danger
      onConfirm={() => {
        setError(null);
        kill.mutate();
      }}
      onClose={onClose}
      data-testid="admin-live-kill-confirm"
    />
  );
}

// ---------------------------------------------------------------------------
// C4 — Blocks list (with Unblock)
// ---------------------------------------------------------------------------

function BlocksList({ blocks }: { blocks: AdminStreamBlock[] }) {
  const [unblockTarget, setUnblockTarget] = useState<AdminStreamBlock | null>(
    null,
  );

  // Always render the section so the testid is stable; the body
  // collapses to an empty-state when there are no blocks.
  return (
    <section
      data-testid="admin-live-blocks"
      className="flex flex-col gap-2 rounded-lg border border-rule bg-bg-card/40 p-3"
    >
      <header className="flex items-baseline justify-between">
        <h3 className="text-sm font-display uppercase tracking-wider text-ink">
          Blocked
        </h3>
        <span className="text-[0.6rem] font-mono text-ink-muted">
          {blocks.length} active
        </span>
      </header>
      {blocks.length === 0 ? (
        <p
          data-testid="admin-live-blocks-empty"
          className="text-xs font-mono text-ink-muted"
        >
          No active blocks.
        </p>
      ) : (
        <ul className="flex flex-col gap-1">
          {blocks.map((b, i) => (
            <li
              key={`${b.ip}-${b.kind}-${i}`}
              data-testid="admin-live-block-row"
              className="grid grid-cols-[1fr_auto] gap-2 items-center rounded border border-rule/60 bg-surface/40 px-2 py-1.5"
            >
              <div className="flex flex-col text-[0.6rem] font-mono text-ink-muted min-w-0">
                <span className="text-ink truncate">
                  {b.ip} <span className="text-ink-muted">· {b.kind}</span>
                </span>
                <span>
                  {b.match_id ? `match ${b.match_id}` : "live stream"}
                  {b.slot ? ` · ${b.slot}` : ""}
                  {b.remaining_seconds !== undefined
                    ? ` · expires in ${Math.round(b.remaining_seconds)}s`
                    : ""}
                </span>
              </div>
              <button
                type="button"
                onClick={() => setUnblockTarget(b)}
                data-testid="admin-live-block-unblock"
                className="rounded border border-rule text-ink-muted hover:text-ink hover:bg-surface px-2 py-0.5 text-[0.65rem] font-display uppercase tracking-wider"
              >
                Unblock
              </button>
            </li>
          ))}
        </ul>
      )}
      {unblockTarget ? (
        <UnblockConfirm
          block={unblockTarget}
          onClose={() => setUnblockTarget(null)}
        />
      ) : null}
    </section>
  );
}

function UnblockConfirm({
  block,
  onClose,
}: {
  block: AdminStreamBlock;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const unblock = useMutation({
    mutationFn: () =>
      unblockAdminStream({
        ip: block.ip,
        kind: (block.kind || "live") as "live" | "vod-hls" | "vod-mp4",
        match_id: block.match_id ?? null,
        slot: block.slot ?? null,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: unscopedKey("admin-streams"),
      });
      onClose();
    },
    onError: (err) => setError(formatErr(err)),
  });
  return (
    <ConfirmDialog
      title="Unblock this entry?"
      message={
        <>
          <p>
            Allows <strong>{block.ip}</strong> to reconnect to{" "}
            <strong>{block.match_id ? `${block.match_id}/${block.slot ?? "live"}` : "live"}</strong>{" "}
            immediately.
          </p>
          {error ? (
            <p
              data-testid="admin-live-unblock-error"
              className="mt-2 text-destructive font-mono text-xs"
            >
              {error}
            </p>
          ) : null}
        </>
      }
      confirmLabel={unblock.isPending ? "Unblocking…" : "Unblock"}
      onConfirm={() => {
        setError(null);
        unblock.mutate();
      }}
      onClose={onClose}
      data-testid="admin-live-unblock-confirm"
    />
  );
}

// ---------------------------------------------------------------------------
// C8 — Live diagnostics block
// ---------------------------------------------------------------------------

function LiveDiagnosticsBlock({
  diagnostics,
  liveDiagnostics,
  liveDiagnosticsFetching,
  liveDiagnosticsError,
  onRefresh,
}: {
  diagnostics: AdminDiagnostics | undefined;
  liveDiagnostics: AdminLiveDiagnostics | undefined;
  liveDiagnosticsFetching: boolean;
  liveDiagnosticsError: unknown;
  onRefresh: () => void;
}) {
  const liveEvents = useMemo(
    () =>
      (diagnostics?.recent_activity ?? [])
        .filter((event) => {
          const kind = String(event.metadata?.kind ?? "").toLowerCase();
          const type = event.event_type.toLowerCase();
          return kind === "live" || type.startsWith("live.") || type === "stream.killed" || (type.startsWith("stream.") && kind === "live");
        })
        .slice(0, 8),
    [diagnostics?.recent_activity],
  );
  const publisher = liveDiagnostics?.publisher;
  const publisherReady = Boolean(publisher?.ready);
  const connections = liveDiagnostics?.rtmp_connections ?? [];
  const rejections = (liveDiagnostics?.recent_rejections ?? []).slice(0, 5);
  const otherPaths = (liveDiagnostics?.paths ?? []).filter(
    (path) => String(path.name ?? "") !== String(liveDiagnostics?.stream_path ?? ""),
  );

  return (
    <section
      data-testid="admin-live-diagnostics"
      className="flex flex-col gap-3 rounded-lg border border-rule bg-bg-card/40 p-3"
    >
      <header className="flex items-baseline justify-between gap-2 flex-wrap">
        <div>
          <h3 className="text-sm font-display uppercase tracking-wider text-ink">
            Diagnostics
          </h3>
          <p className="text-[0.65rem] font-mono text-ink-muted">
            MediaMTX ingest health + live stream activity
          </p>
        </div>
        <button
          type="button"
          onClick={onRefresh}
          disabled={liveDiagnosticsFetching}
          data-testid="admin-live-diagnostics-refresh"
          className="rounded border border-rule text-ink-muted hover:text-ink hover:bg-surface px-2 py-1 text-[0.6rem] font-display uppercase tracking-wider disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {liveDiagnosticsFetching ? "Checking…" : "Diagnose"}
        </button>
      </header>

      {liveDiagnosticsError ? (
        <p
          data-testid="admin-live-diagnostics-error"
          className="text-destructive font-mono text-xs"
        >
          {formatErr(liveDiagnosticsError)}
        </p>
      ) : null}

      <div className="flex flex-wrap gap-2">
        <DiagnosticPill
          testid="admin-live-mediamtx-pill"
          tone={liveDiagnostics?.reachable ? "good" : "bad"}
          label={liveDiagnostics?.reachable ? "MediaMTX reachable" : "MediaMTX unreachable"}
        />
        <DiagnosticPill
          testid="admin-live-publisher-pill"
          tone={publisherReady ? "good" : "warn"}
          label={
            publisher
              ? `Publisher ${publisherReady ? "ready" : "not ready"}${publisher.bytes_received != null ? ` · ${formatBytesShort(Number(publisher.bytes_received))} in` : ""}`
              : "No publisher on configured path"
          }
        />
        <DiagnosticPill
          testid="admin-live-stream-path-pill"
          tone="muted"
          label={`Path ${redactStreamPath(liveDiagnostics?.stream_path)}`}
        />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-2">
        <DiagnosticList title="RTMP connections" count={connections.length} testid="admin-live-rtmp-connections">
          {connections.length === 0 ? (
            <DiagEmpty copy="No active RTMP connections." />
          ) : (
            connections.map((conn, index) => (
              <DiagnosticRow key={`${String(conn.remote_addr ?? "?")}-${index}`} testid="admin-live-rtmp-row">
                <code>{String(conn.remote_addr ?? "?")}</code>
                <span>state={String(conn.state ?? "unknown")}</span>
                <span>path={redactStreamPath(conn.path)}</span>
                <span>in={formatBytesShort(Number(conn.bytes_received ?? 0))}</span>
                <span>out={formatBytesShort(Number(conn.bytes_sent ?? 0))}</span>
              </DiagnosticRow>
            ))
          )}
        </DiagnosticList>

        <DiagnosticList title="Recent auth rejections" count={rejections.length} testid="admin-live-auth-rejections">
          {rejections.length === 0 ? (
            <DiagEmpty copy="No recent auth rejections." />
          ) : (
            rejections.map((rej, index) => (
              <DiagnosticRow key={`${String(rej.ts ?? "")}-${index}`} testid="admin-live-auth-rejection-row">
                <code>{String(rej.ts ?? "")}</code>
                <span>ip={String(rej.ip ?? "?")}</span>
                <span>{String(rej.action ?? "?")}/{String(rej.protocol ?? "?")}</span>
                <span>path={redactStreamPath(rej.path)}</span>
                <strong>{String(rej.reason ?? "Rejected")}</strong>
              </DiagnosticRow>
            ))
          )}
        </DiagnosticList>
      </div>

      {otherPaths.length > 0 ? (
        <DiagnosticList title="Other MediaMTX paths" count={otherPaths.length} testid="admin-live-other-paths">
          {otherPaths.map((path, index) => (
            <DiagnosticRow key={`${String(path.name ?? "")}-${index}`} testid="admin-live-path-row">
              <code>{redactStreamPath(path.name)}</code>
              <span>ready={path.ready ? "yes" : "no"}</span>
            </DiagnosticRow>
          ))}
        </DiagnosticList>
      ) : null}

      <DiagnosticList title="Live activity" count={liveEvents.length} testid="admin-live-activity">
        {liveEvents.length === 0 ? (
          <DiagEmpty copy="No live stream activity in the recent feed." />
        ) : (
          liveEvents.map((event, index) => (
            <DiagnosticRow
              key={`${event.timestamp}-${event.event_type}-${index}`}
              testid="admin-live-activity-row"
              severity={event.severity}
            >
              <span>{formatTime(event.timestamp)}</span>
              <span>{event.event_type}</span>
              <span>{event.message || "Live event"}</span>
              {event.actor ? <span>by {event.actor}</span> : null}
            </DiagnosticRow>
          ))
        )}
      </DiagnosticList>

      <p className="rounded border border-rule/60 bg-surface/40 px-2 py-1.5 text-[0.6rem] font-mono text-ink-muted">
        Hint: RTMP state <code>read</code> means the handshake completed; <code>idle</code> or <code>preRead</code> means the camera opened the socket but has not published yet.
      </p>
    </section>
  );
}

function DiagnosticPill({
  testid,
  tone,
  label,
}: {
  testid: string;
  tone: "good" | "warn" | "bad" | "muted";
  label: string;
}) {
  const classes = {
    good: "border-good/50 text-good bg-good/10",
    warn: "border-accent/50 text-accent bg-accent/10",
    bad: "border-destructive/50 text-destructive bg-destructive/10",
    muted: "border-rule text-ink-muted bg-surface/30",
  }[tone];
  return (
    <span
      data-testid={testid}
      data-tone={tone}
      className={`rounded border px-2 py-1 text-[0.6rem] font-display uppercase tracking-wider ${classes}`}
    >
      {label}
    </span>
  );
}

function DiagnosticList({
  title,
  count,
  testid,
  children,
}: {
  title: string;
  count: number;
  testid: string;
  children: ReactNode;
}) {
  return (
    <div data-testid={testid} className="flex flex-col gap-1.5 rounded border border-rule/60 bg-surface/30 p-2">
      <div className="flex items-baseline justify-between">
        <h4 className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
          {title}
        </h4>
        <span className="text-[0.6rem] font-mono text-ink-muted">{count}</span>
      </div>
      <div className="flex flex-col gap-1">{children}</div>
    </div>
  );
}

function DiagnosticRow({
  testid,
  severity,
  children,
}: {
  testid: string;
  severity?: string;
  children: ReactNode;
}) {
  const tone = severity === "warning" ? "border-accent/50" : severity === "error" ? "border-destructive/50" : "border-rule/60";
  return (
    <div
      data-testid={testid}
      data-severity={severity ?? "info"}
      className={`flex flex-wrap gap-x-2 gap-y-1 rounded border ${tone} bg-bg-card/50 px-2 py-1.5 text-[0.6rem] font-mono text-ink-muted`}
    >
      {children}
    </div>
  );
}

function DiagEmpty({ copy }: { copy: string }) {
  return <p className="text-[0.6rem] font-mono text-ink-muted">{copy}</p>;
}

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------

function formatErr(err: unknown): string {
  if (err instanceof ApiError) {
    return typeof err.detail === "string" ? err.detail : `HTTP ${err.status}`;
  }
  return "Request failed";
}

function formatBps(bps: number): string {
  if (!Number.isFinite(bps) || bps <= 0) return "0 bps";
  const units = ["bps", "Kbps", "Mbps", "Gbps"];
  let v = bps;
  let i = 0;
  while (v >= 1000 && i < units.length - 1) {
    v /= 1000;
    i += 1;
  }
  return `${v.toFixed(v < 10 ? 1 : 0)} ${units[i]}`;
}

function formatBytesShort(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  let v = bytes;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v.toFixed(v < 10 ? 1 : 0)} ${units[i]}`;
}

function formatTime(ts: string): string {
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return ts;
  return d.toLocaleString([], {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function redactStreamPath(value: unknown): string {
  const raw = String(value ?? "").trim();
  if (!raw) return "—";
  const parts = raw.split("/").filter(Boolean);
  if (parts.length === 0) return "—";
  if (parts.length === 1) return "••••";
  return `${parts[0]}/••••`;
}

function getNested(obj: Record<string, unknown>, path: string[]): unknown {
  let cur: unknown = obj;
  for (const k of path) {
    if (cur && typeof cur === "object" && k in (cur as Record<string, unknown>)) {
      cur = (cur as Record<string, unknown>)[k];
    } else {
      return undefined;
    }
  }
  return cur;
}

// ---------------------------------------------------------------------------
// M12c — per-team team picker + rotate
// ---------------------------------------------------------------------------

type AdminTeamRow = {
  id: string;
  slug: string;
  name: string;
  public?: boolean;
  stream_key?: string | null;
};

/**
 * Compact roster of teams with their live-stream affordances. The picker
 * is a flat list (single-club app — there aren't enough teams to warrant
 * a search UI); each row exposes a Rotate-key action that goes through
 * ConfirmDialog.
 */
function AdminTeamsLiveStrip() {
  const teamsQuery = useQuery({
    queryKey: unscopedKey("admin-teams-live"),
    queryFn: () => apiFetch<AdminTeamRow[]>("/api/admin/teams", { scope: false }),
    staleTime: 60_000,
  });

  return (
    <section
      data-testid="admin-live-teams"
      className="rounded-lg border border-rule bg-bg-card p-3"
    >
      <header className="flex items-center justify-between gap-2 pb-2">
        <h3 className="text-sm font-display uppercase tracking-wider text-ink">
          Per-team broadcasts
        </h3>
        <span className="text-[0.65rem] font-mono uppercase tracking-wide text-ink-muted">
          {teamsQuery.data?.length ?? 0} teams
        </span>
      </header>
      {teamsQuery.isLoading ? (
        <p className="text-ink-muted text-xs font-mono">Loading teams…</p>
      ) : teamsQuery.isError ? (
        <p className="text-destructive text-xs font-mono">
          Couldn’t load teams.
        </p>
      ) : (teamsQuery.data ?? []).length === 0 ? (
        <p className="text-ink-muted text-xs font-mono">
          No teams yet — create one in /admin/teams.
        </p>
      ) : (
        <ul role="list" className="flex flex-col gap-1.5">
          {teamsQuery.data!.map((team) => (
            <li key={team.id}>
              <AdminTeamLiveRow team={team} />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function AdminTeamLiveRow({ team }: { team: AdminTeamRow }) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(false);
  const [rotated, setRotated] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const onRotate = async () => {
    setPending(true);
    setError(null);
    try {
      const resp = await rotateTeamStreamKey(team.id);
      setRotated(resp.stream_key);
      // Refresh the team roster so the "no key" → "keyed" badge updates
      // without a manual reload.
      await queryClient.invalidateQueries({
        queryKey: unscopedKey("admin-teams-live"),
      });
      setOpen(false);
    } catch (err) {
      setError(
        err instanceof ApiError && typeof err.detail === "string"
          ? err.detail
          : "Couldn't rotate the team stream key.",
      );
    } finally {
      setPending(false);
    }
  };

  return (
    <article
      data-testid="admin-live-team-row"
      data-team-slug={team.slug}
      className="flex flex-wrap items-center justify-between gap-2 rounded border border-rule bg-surface px-3 py-2"
    >
      <div className="flex min-w-0 flex-col gap-0.5">
        <p className="font-display text-sm text-ink truncate">{team.name}</p>
        <p className="font-mono text-[0.6rem] uppercase tracking-mono text-ink-muted">
          live/{team.slug}
          {team.public ? " · public" : " · private"}
          {team.stream_key ? " · keyed" : " · no key"}
        </p>
        {rotated ? (
          <p
            data-testid="admin-live-team-rotated"
            className="font-mono text-[0.65rem] text-ink"
          >
            New key:{" "}
            <code className="rounded bg-bg-card px-1 py-0.5 text-ink">
              {rotated}
            </code>
          </p>
        ) : null}
        {error ? (
          <p className="font-mono text-[0.65rem] text-destructive">{error}</p>
        ) : null}
      </div>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="admin-live-team-rotate"
        className="rounded border border-destructive/40 bg-destructive/10 px-2 py-1 text-[0.65rem] font-display uppercase tracking-wider text-destructive hover:bg-destructive/20"
      >
        Rotate key
      </button>
      {open ? (
        <ConfirmDialog
          title={`Rotate ${team.name}'s stream key?`}
          message={
            <p>
              Generates a new key and immediately rejects any in-progress
              broadcast using the previous key. The team admin will need
              the new key before they can publish again.
            </p>
          }
          confirmLabel={pending ? "Rotating…" : "Rotate key"}
          danger
          onConfirm={() => void onRotate()}
          onClose={() => {
            if (pending) return;
            setOpen(false);
            setError(null);
          }}
          data-testid="admin-live-team-rotate-confirm"
        />
      ) : null}
    </article>
  );
}
